// Therapy material references images bundled in the KoALA app by their Flutter asset path, e.g.
// "assets/images/therapy/01.png". The editor keeps a copy of those images in public/app-assets
// (see scripts/sync-app-assets.js).
const APP_ASSET_PATH = /^assets\/images\/(?:[\w-]+\/)*[\w-]+(?:\.[\w-]+)*\.(?:png|jpe?g)$/i;

export const resolveAppAssetUrl = (path?: string): string | null => {
    const normalized = path?.trim().replace(/^\.?\//, '');
    if (!normalized || !APP_ASSET_PATH.test(normalized)) {
        return null;
    }

    return `${process.env.PUBLIC_URL ?? ''}/app-assets/${normalized.slice('assets/'.length)}`;
};

export const getAssetFileName = (path?: string): string => path?.split('/').pop() ?? '';

export type PdfImage = {
    dataUrl: string;
    width: number;
    height: number;
};

const MAX_PDF_IMAGE_WIDTH = 1600;

const loadImageElement = (src: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error(`Could not decode image ${src}`));
        image.src = src;
    });

// Scales the image down and converts it to JPEG on a white background, which keeps PDFs small.
// Resolves to null when the image is missing, so the PDF can show a placeholder instead.
export const loadPdfImage = async (url: string): Promise<PdfImage | null> => {
    try {
        const response = await fetch(url);
        if (!response.ok) {
            return null;
        }

        const objectUrl = URL.createObjectURL(await response.blob());
        try {
            const image = await loadImageElement(objectUrl);
            const scale = Math.min(1, MAX_PDF_IMAGE_WIDTH / image.naturalWidth);
            const width = Math.max(1, Math.round(image.naturalWidth * scale));
            const height = Math.max(1, Math.round(image.naturalHeight * scale));
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const context = canvas.getContext('2d');
            if (!context) {
                return null;
            }

            context.fillStyle = '#ffffff';
            context.fillRect(0, 0, width, height);
            context.drawImage(image, 0, 0, width, height);
            return { dataUrl: canvas.toDataURL('image/jpeg', 0.85), width, height };
        } finally {
            URL.revokeObjectURL(objectUrl);
        }
    } catch {
        return null;
    }
};
